import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store/useStore';
import { isWeekend } from '@mui/x-scheduler-headless/use-adapter';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { getDayList } from '@mui/x-scheduler-headless/get-day-list';
import { eventTimelinePremiumViewSelectors } from '@mui/x-scheduler-headless-premium/event-timeline-premium-selectors';
import { useEventTimelinePremiumStoreContext } from '@mui/x-scheduler-headless-premium/use-event-timeline-premium-store-context';
import { useEventTimelinePremiumStyledContext } from "../../EventTimelinePremiumStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DaysHeaderRoot = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'DaysHeader'
})({
  display: 'flex',
  width: 'calc(var(--unit-count) * var(--days-cell-width))'
});
const DayHeaderCell = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'DaysHeaderCell'
})(({
  theme
}) => ({
  width: 'var(--days-cell-width)',
  textAlign: 'center',
  padding: theme.spacing(1),
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  '&:not(:last-child)': {
    borderRight: `1px solid ${(theme.vars || theme).palette.divider}`
  }
}));
const DayHeaderTime = styled('time', {
  name: 'MuiEventTimeline',
  slot: 'DaysHeaderTime'
})({
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
});
const WeekDay = styled('span', {
  name: 'MuiEventTimeline',
  slot: 'DaysHeaderWeekDay'
})(({
  theme
}) => ({
  margin: 0,
  fontSize: theme.typography.body2.fontSize,
  color: (theme.vars || theme).palette.text.secondary,
  '&[data-weekend]': {
    color: (theme.vars || theme).palette.error.main
  }
}));
const DayNumber = styled('span', {
  name: 'MuiEventTimeline',
  slot: 'DaysHeaderDayNumber'
})(({
  theme
}) => ({
  margin: 0,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  color: (theme.vars || theme).palette.text.primary
}));
const MonthStart = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'DaysHeaderMonthStart'
})(({
  theme
}) => _extends({
  fontSize: theme.typography.caption.fontSize,
  color: (theme.vars || theme).palette.text.secondary,
  background: (theme.vars || theme).palette.grey[100],
  height: '100%',
  margin: 0,
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  top: 0,
  bottom: 0,
  left: 0
}, theme.applyStyles('dark', {
  background: theme.palette.grey[800]
})));
const MonthStartLabel = styled('p', {
  name: 'MuiEventTimeline',
  slot: 'DaysHeaderMonthStartLabel'
})({
  transform: 'rotate(-90deg)'
});
export function DaysHeader(props) {
  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventTimelinePremiumStoreContext();
  const {
    classes
  } = useEventTimelinePremiumStyledContext();

  // Selector hooks
  const viewConfig = useStore(store, eventTimelinePremiumViewSelectors.config);

  // Feature hooks
  const days = React.useMemo(() => getDayList({
    adapter,
    start: viewConfig.start,
    end: viewConfig.end
  }), [adapter, viewConfig]);
  return /*#__PURE__*/_jsx(DaysHeaderRoot, _extends({
    className: classes.daysHeader
  }, props, {
    children: days.map((day, index) => /*#__PURE__*/_jsxs(DayHeaderCell, {
      className: classes.daysHeaderCell,
      children: [(adapter.getDate(day.value) === 1 || index === 0) && /*#__PURE__*/_jsx(MonthStart, {
        className: classes.daysHeaderMonthStart,
        children: /*#__PURE__*/_jsx(MonthStartLabel, {
          className: classes.daysHeaderMonthStartLabel,
          children: adapter.format(day.value, 'month3Letters')
        })
      }), /*#__PURE__*/_jsxs(DayHeaderTime, {
        dateTime: day.key,
        className: classes.daysHeaderTime,
        children: [/*#__PURE__*/_jsx(WeekDay, {
          className: classes.daysHeaderWeekDay,
          "data-weekend": isWeekend(adapter, day.value) ? '' : undefined,
          children: adapter.format(day.value, 'weekday1Letter')
        }), /*#__PURE__*/_jsx(DayNumber, {
          className: classes.daysHeaderDayNumber,
          "data-weekend": isWeekend(adapter, day.value) ? '' : undefined,
          children: adapter.format(day.value, 'dayOfMonth')
        })]
      })]
    }, day.key))
  }));
}